import { type ReactNode, useState } from 'react';

import { AppIcon, InfoModal } from '@backrs_ui/common-ui';

import { Box, type SxProps, type Theme, Typography } from '@mui/material';

import {
  cardSx,
  dividerSx,
  headerTextSx,
  mutedTextSx,
  titleSx,
  withClickable,
} from './analytics.styles';

type AnalyticsCardProps = Readonly<{
  title: string;
  subtitle?: string;
  /** Caveats and definitions, shown on the header info icon. Falsy entries drop out. */
  info?: readonly (string | false | null | undefined)[];
  // Rendered between the header and its divider.
  belowHeader?: ReactNode;
  id?: string;
  onClick?: () => void;
  children: ReactNode;
}>;

export const AnalyticsCard = ({
  title,
  subtitle,
  info,
  belowHeader,
  id,
  onClick,
  children,
}: AnalyticsCardProps) => (
  <Box id={id} onClick={onClick} sx={withClickable(cardSx, onClick)}>
    <Box sx={cardHeaderRowSx}>
      <Box sx={headerTextSx}>
        <Typography variant="text-lg-semibold" sx={titleSx}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="text-sm-regular" sx={mutedTextSx}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      <CardInfoIcon info={info} title={title} />
    </Box>
    {belowHeader}
    <Box sx={dividerSx} />
    <Box sx={cardBodySx}>{children}</Box>
  </Box>
);

const slug = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

/** Info icon, made interactive only when the card has notes to show. */
export const CardInfoIcon = ({
  info,
  title,
}: Readonly<{
  info?: readonly (string | false | null | undefined)[];
  title: string;
}>) => {
  const [isOpen, setIsOpen] = useState(false);
  const notes = (info ?? []).filter((note): note is string => Boolean(note));
  const icon = (
    <AppIcon iconName="InfoCircle" diameter="20px" color="Colors/Foreground/fg-quinary" />
  );

  if (notes.length === 0) return icon;

  return (
    <>
      <Box
        id={`analytics-card-info-${slug(title)}`}
        sx={infoTriggerSx}
        onClick={(event) => {
          event.stopPropagation();
          setIsOpen(true);
        }}
      >
        {icon}
      </Box>
      {/* The modal portals out of the card, but its clicks still bubble the React tree into
          the card's onClick, so they stop here. display:contents keeps the wrapper out of layout. */}
      <Box sx={modalGuardSx} onClick={(event) => event.stopPropagation()}>
        <InfoModal
          open={isOpen}
          onClose={() => setIsOpen(false)}
          width="sm"
          hideCircleImage
          headerContent={<Typography variant="text-lg-semibold">{title}</Typography>}
          bodyContent={
            <Box sx={infoListSx}>
              {notes.map((note) => (
                <Typography key={note} variant="text-md-regular" sx={mutedTextSx}>
                  {note}
                </Typography>
              ))}
            </Box>
          }
        />
      </Box>
    </>
  );
};

const infoTriggerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  flexShrink: 0,
  cursor: 'pointer',
  padding: backrs.spacing['spacing-xs'],
  margin: `-${backrs.spacing['spacing-xs']}`,
  borderRadius: backrs.radius['radius-full'],
  '&:hover': {
    backgroundColor: backrs.colors['Colors/Background/bg-secondary-hover'],
  },
});

const modalGuardSx: SxProps<Theme> = {
  display: 'contents',
};

const infoListSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
});

/**
 * Cards in a row share the tallest card's height, so the body centres in
 * whatever space it is given rather than leaving the surplus below it.
 */
const cardBodySx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: backrs.spacing['spacing-2xl'],
  flex: 1,
  width: '100%',
  minWidth: 0,
});

const cardHeaderRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-md'],
  width: '100%',
});
